import { MoreVert } from "@mui/icons-material";
import {
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { differenceInDays } from "date-fns";
import UpdateFeedEventDialog from "features/feedPurchaseDetail/UpdateFeedEventDialog";
import UpdateFeedPurchaseEventDialog from "features/feedPurchaseDetail/UpdateFeedPurchaseEventDialog";
import UpdateFertilizerEventDialog from "features/fertilizerInput/UpdateFertilizerEventDialog";
import UpdateFieldPreparationEventDialog from "features/fieldPreparationInput/UpdateFieldPreparationEventDialog";
import UpdateReseedEventDialog from "features/singleReseedEventInput/UpdateReseedEventDialog";
import UpdateYieldEventDialog from "features/yieldInput/UpdateYieldEventDialog";
import {
  FEED_EVENT_REJECTION_PERIOD_DAYS,
  FIELD_PREPARATION_EVENT_REJECTION_PERIOD_DAYS,
  FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS,
} from "model";
import { useState } from "react";
import { getFormattedDate } from "../../common/utils/getFormattedTime";
import { useAppSelector } from "../../model";
import { CombinedEvent } from "./CombinedInputsOutputsList";
import {
  FeedEventTableCell,
  FeedPurchaseEventTableCell,
  FertilizerEventTableCell,
  FieldPreparationEventTableCell,
  ReseedEventTableCell,
  YieldEventTableCell,
} from "./TableCellListItems";

interface CombinedEventListItemProps {
  combinedEvent: CombinedEvent;
  getEventIcon: (type: CombinedEvent["type"]) => React.ReactNode;
  getEventTypeLabel: (type: CombinedEvent["type"]) => string;
}

export default function CombinedEventListItem({
  combinedEvent,
  getEventIcon,
  getEventTypeLabel,
}: CombinedEventListItemProps) {
  const locale = useAppSelector((state) => state.app.locale);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }

  // Calculate canEdit based on event type and date
  const canEdit = (() => {
    const creationDate = combinedEvent.event.creationDate
      ? new Date(combinedEvent.event.creationDate)
      : Date.now();

    const daysSinceCreation = differenceInDays(Date.now(), creationDate);

    switch (combinedEvent.type) {
      case "reseed":
        return daysSinceCreation <= FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS;
      case "feed":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
      case "feedPurchase":
        return (
          daysSinceCreation <= FIELD_PREPARATION_EVENT_REJECTION_PERIOD_DAYS
        );
      case "yield":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
      case "fertilizer":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
      case "fieldPreparation":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
    }
  })();

  const formattedDate = getFormattedDate(
    combinedEvent.date.toISOString(),
    locale
  );

  const getEventDetails = () => {
    switch (combinedEvent.type) {
      case "reseed":
        return <ReseedEventTableCell reseedEvent={combinedEvent.event} />;
      case "feed":
        return <FeedEventTableCell feedEvent={combinedEvent.event} />;
      case "feedPurchase":
        return (
          <FeedPurchaseEventTableCell feedPurchaseEvent={combinedEvent.event} />
        );
      case "yield":
        return <YieldEventTableCell yieldEvent={combinedEvent.event} />;
      case "fertilizer":
        return (
          <FertilizerEventTableCell fertilizerEvent={combinedEvent.event} />
        );
      case "fieldPreparation":
        return (
          <FieldPreparationEventTableCell
            fieldPreparationEvent={combinedEvent.event}
          />
        );
    }
  };

  return (
    <ListItem
      key={`combined-${combinedEvent.type}-${combinedEvent.event.guid}`}
      sx={{
        flexDirection: "column",
        alignItems: "stretch",
        py: 2,
        px: 2,
        borderBottom: "1px solid #e0e0e0",
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
        <ListItemIcon sx={{ minWidth: 48, pt: 0.5 }}>
          {getEventIcon(combinedEvent.type)}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {formattedDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getEventTypeLabel(combinedEvent.type)}
              </Typography>
            </Box>
          }
          secondary={
            <Box sx={{ mt: 1.5, width: "100%" }}>{getEventDetails()}</Box>
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      </Box>
      {canEdit && (
        <ListItemSecondaryAction
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
          }}
        >
          <IconButton
            edge="end"
            aria-haspopup="true"
            onClick={openMenu}
            id={combinedEvent.event.guid}
            size="small"
          >
            <MoreVert />
          </IconButton>
          {combinedEvent.type === "reseed" && (
            <UpdateReseedEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              reseedEvent={combinedEvent.event}
            />
          )}
          {combinedEvent.type === "feed" && (
            <UpdateFeedEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              feedEvent={combinedEvent.event}
            />
          )}
          {combinedEvent.type === "feedPurchase" && (
            <UpdateFeedPurchaseEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              feedPurchaseEvent={combinedEvent.event}
            />
          )}
          {combinedEvent.type === "yield" && (
            <UpdateYieldEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              yieldEvent={combinedEvent.event}
            />
          )}
          {combinedEvent.type === "fertilizer" && (
            <UpdateFertilizerEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              fertilizerEvent={combinedEvent.event}
            />
          )}
          {combinedEvent.type === "fieldPreparation" && (
            <UpdateFieldPreparationEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              fieldPreparationEvent={combinedEvent.event}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
}
